import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, FileUp, Loader2, RefreshCw } from "lucide-react";

import { fetchOpciDioStatus } from "@/api/client";
import type { OpciDioStatus, ReadinessItem } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { BadgeStatus } from "@/components/ui/badge-status";
import { countHr } from "@/lib/pluralHr";
import { useIntakeStore } from "./useIntakeStore";
import { useRecognizeDocs } from "./useRecognizeDocs";

/**
 * Step-2 completeness panel — "što još treba odlučiti/donijeti prije
 * generiranja". Renders the readiness block (blockers/warnings) the backend
 * computes in ``/api/opci-dio/status`` + a one-line document summary.
 * Deliberately NOT here (F2): the full per-slot checklist — that's the
 * Donesi console's job, repeating it made the panel a second console.
 * Advisory: it never disables the generate button, it tells the truth next
 * to it.
 */
export function CompletenessPanel({
  projectId,
  params,
}: {
  projectId: string;
  params: {
    building_type: string;
    building_subtype?: string | null;
    context?: string | null;
    work_type?: string;
  };
}) {
  const navigate = useNavigate();
  const intake = useIntakeStore((s) => s.get(projectId));
  const setStatus = useIntakeStore((s) => s.setOpciDioStatus);

  const refresh = useMutation<OpciDioStatus, Error>({
    mutationFn: () =>
      fetchOpciDioStatus({
        ...params,
        situacija: intake.situacija ?? undefined,
        recognized_docs: intake.recognizedDocs,
      }),
    onSuccess: (status) => setStatus(projectId, status),
  });

  // Inline upload on a "missing document" blocker — the diagnosis and
  // the lever in the same row. Routed through the same recognize path
  // the sources basket uses, so the readiness refresh comes for free.
  const { addPdfFiles, queue, errors } = useRecognizeDocs(projectId, params);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const status = intake.opciDioStatus;
  const readiness = status?.summary?.readiness;

  // "Generirana izjava 1.X traži pregled" ×7 is one fact, not seven —
  // collapse the generation_review warnings into a single row.
  const allWarnings: ReadinessItem[] = readiness?.warnings ?? [];
  const reviewSlots = allWarnings
    .filter((w) => w.kind === "generation_review")
    .map((w) => String((w as { slot?: string }).slot ?? ""))
    .filter(Boolean);
  const otherWarnings = allWarnings.filter((w) => w.kind !== "generation_review");

  // Totals for the one-line summary (the per-row detail lives on Donesi).
  const haveDocs =
    status?.rows.filter((r) => r.state === "prepoznato").length ?? 0;
  const generated =
    status?.rows.filter(
      (r) => r.fulfilled_by === "generate" && r.state !== "nije_potrebno",
    ).length ?? 0;

  return (
    <CollapsibleCard
      className="mt-6"
      icon={<ClipboardCheck className="h-4 w-4 text-ind-600" />}
      title="Spremnost dokumentacije"
      summary={
        readiness ? (
          <BadgeStatus kind={readiness.ready ? "ok" : "warn"}>
            {readiness.ready
              ? "spremno za generiranje"
              : `${readiness.blockers.length} stavki nedostaje`}
          </BadgeStatus>
        ) : (
          <span className="text-stone-400">nije provjereno</span>
        )
      }
    >
        {!status && (
          <div className="flex flex-col items-start gap-2">
            <p className="text-[0.85rem] text-stone-500">
              Provjeri što je skupljeno i što nedostaje prije generiranja —
              sadržaj općeg dijela, konflikti izvora i pokrivenost posebnih
              uvjeta.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ClipboardCheck className="h-4 w-4" />
              )}
              Provjeri spremnost
            </Button>
            {refresh.error && (
              <p className="text-[0.8rem] text-red-700">
                Greška: {refresh.error.message}
              </p>
            )}
          </div>
        )}

        {status && (
          <>
            <div className="mb-3 flex items-center justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
              >
                {refresh.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Osvježi
              </Button>
            </div>

            {/* Hidden input behind every "Učitaj PDF" lever below. */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) void addPdfFiles(files);
                e.target.value = "";
              }}
            />

            {readiness && readiness.blockers.length > 0 && (
              <section className="mb-3">
                <h4 className="mb-1 text-[0.8rem] font-bold uppercase tracking-[0.04em] text-stone-600">
                  Nedostaje / treba odluka
                </h4>
                <ul className="rounded-md border border-amber-200 bg-amber-50 px-3">
                  {readiness.blockers.map((b, i) => (
                    <li
                      key={`${b.kind}-${i}`}
                      className="flex flex-wrap items-center gap-2 border-b border-amber-100 py-2 text-[0.82rem] text-amber-900 last:border-0"
                    >
                      <span>{b.label}</span>
                      {b.kind === "missing_upload" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="ml-auto"
                          disabled={queue.length > 0}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {queue.length > 0 ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FileUp className="h-3.5 w-3.5" />
                          )}
                          Učitaj PDF
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
                {errors.length > 0 && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
                    {errors.map((e, i) => (
                      <p key={i}>Greška: {e}</p>
                    ))}
                  </div>
                )}
              </section>
            )}

            {(otherWarnings.length > 0 || reviewSlots.length > 0) && (
              <section className="mb-3">
                <h4 className="mb-1 text-[0.8rem] font-bold uppercase tracking-[0.04em] text-stone-600">
                  Upozorenja
                </h4>
                <ul className="rounded-md border border-stone-200 bg-white px-3">
                  {reviewSlots.length > 0 && (
                    <li className="border-b border-stone-100 py-2 text-[0.8rem] text-stone-600 last:border-0">
                      {reviewSlots.length === 1
                        ? `Generirana izjava ${reviewSlots[0]} traži pregled`
                        : `${countHr(reviewSlots.length, "generirana izjava", "generirane izjave", "generiranih izjava")} (${reviewSlots[0]}–${reviewSlots[reviewSlots.length - 1]}) traži pregled prije predaje`}
                    </li>
                  )}
                  {otherWarnings.map((w, i) => (
                    <li
                      key={`${w.kind}-${i}`}
                      className="border-b border-stone-100 py-2 text-[0.8rem] text-stone-600 last:border-0"
                    >
                      {w.label}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* One line instead of the full per-slot checklist — the console
                on Donesi already shows every row with its evidence; here the
                architect only needs the totals and the way back. */}
            <p className="flex flex-wrap items-center gap-x-1.5 border-t border-stone-100 pt-3 text-[0.8rem] text-stone-500">
              <span>
                Imaš {countHr(haveDocs, "dokument", "dokumenta", "dokumenata")}
                {generated > 0 && <> · {generated} generira aplikacija</>}.
              </span>
              <button
                type="button"
                className="font-semibold text-ind-700 hover:text-ind-800"
                onClick={() => navigate(`/project/${projectId}/donesi`)}
              >
                Pregled po stavkama je na koraku Donesi →
              </button>
            </p>
          </>
        )}
    </CollapsibleCard>
  );
}
